import { getYearKey } from "./date-utils";
import { parseDateInput } from "@utils/format-utils";

/**
 * Aggregates data by year from any date field, summing a specified count field
 * Returns chart-ready format: { id, value, label, count }
 * 
 * @param data - Raw data array
 * @param dateField - Date field name (e.g., "purchase_date")
 * @param countField - Field to sum (e.g., "cost", "count")
 * 
 * @example
 * aggregateDateByYear(data, "purchase_date", "cost") // [{ id: "2024", label: "2024", count: 250000 }, ...]
 */
export const aggregateDateByYear = (
  data: Record<string, any>[],
  dateField: string,
  countField: string = "count"
): any[] => {
  const yearMap = new Map<string, number>();

  data.forEach((item) => {
    const yearKey = getYearKey(item[dateField]);
    if (!yearKey || yearKey === "NaN" || yearKey.length !== 4) return;
    const value = Number(item[countField]) || 0;
    yearMap.set(yearKey, (yearMap.get(yearKey) || 0) + value);
  });

  return Array.from(yearMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([year, value]) => ({
      id: year,
      value: year,
      label: year,
      count: value,  // BarChart uses .count for the numeric value
    }));
};

/**
 * Aggregates data by month from any date field, summing a specified count field
 * Returns chart-ready format with month labels: { id, value, label, count }
 * 
 * @param data - Raw data array
 * @param dateField - Date field name (e.g., "purchase_date")
 * @param countField - Field to sum (e.g., "cost", "count")
 * 
 * @example
 * aggregateDateByMonth(data, "purchase_date", "cost") // [{ id: "2024-01", label: "Jan 2024", count: 15000 }, ...]
 */
export const aggregateDateByMonth = (
  data: Record<string, any>[],
  dateField: string,
  countField: string = "count"
): any[] => {
  const monthMap = new Map<string, number>();

  data.forEach((item) => {
    const date = parseDateInput(item[dateField]);
    if (date) {
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const value = Number(item[countField]) || 0;
      monthMap.set(monthKey, (monthMap.get(monthKey) || 0) + value);
    }
  });

  return Array.from(monthMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, value]) => {
      const [year, monthNumber] = month.split("-").map(Number);
      const date = new Date(year, monthNumber - 1, 1);
      const label = date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      return {
        id: month,
        value: month,
        label,
        count: value,  // BarChart uses .count for the numeric value
      };
    });
};

